import * as amplitude from '@amplitude/analytics-browser';
import { pageUrlEnrichmentPlugin } from '@amplitude/plugin-page-url-enrichment-browser';
import type LogRocketSdk from 'logrocket';
import { useCallback } from 'react';
import { config } from '@/config';
import type { DecoratedProfile } from '@/contexts';
import type { PaywallFeature } from '@/features/billing/gates/gates';
import type { CheckoutTarget } from '@/features/billing/lib/checkoutAnalytics';
import type { StepId as PlannerOnboardingStep } from '@/features/experiences/components/PlannerOnboarding/steps';
import type {
  PlannerCheckoutPlacement,
  PlannerLandingVariant,
} from '@/features/experiences/pages/PlannerLandingPage/PlannerLandingConfigContext';
import type { ArtifactType } from '@/features/magic/contexts/ArtifactContext/artifact.lib';
import type { ArtifactOutput, MagicOutput } from '@/features/magic/outputs.lib';
import type { LaunchOption } from '@/hooks/useLauncher/types';
import { Sentry } from '@/lib';
import {
  redactGraphQLRequestBody,
  redactSensitiveUrls,
  redactSensitiveUrlsDeep,
} from '@/lib/redactSensitiveData';
import {
  notifySessionRecordingStarted,
  shouldRecordSession,
} from '@/lib/sessionRecording';
import type { ScheduleOption } from '@/pages/Dashboard/components/CreateScheduleModal/types';
import type { OnboardingFlow } from '@/pages/Schedule/components/OnboardingModal';
import type { DraftComposer } from '@/pages/Schedule/stores';
import { OrganizationType } from '@/types/gql.generated';
import type {
  BlockLayouts,
  ExperienceConfigName,
  ExperienceLayouts,
  ExperiencePrintableLayouts,
  ExperienceTypes,
  PlatformTypes,
  ScheduleHeaderId,
  ScheduleType,
} from '@/types/gql.generated';
import type { ConversionType } from '@/utils/convertEntry';
import { getClientName } from '@/utils/getClientName';

type Page =
  | 'schedule:list'
  | 'schedule'
  | 'schedule:prompt'
  | 'schedule:members'
  | 'schedule:messages'
  | 'schedule:snippets'
  | 'schedule:snippet'
  | 'schedule:sources'
  | 'entry'
  | 'profile'
  | 'login'
  | 'register'
  | 'reset-password-request'
  | 'reset-password'
  | 'magic'
  | 'magic:detail';

type Product =
  | 'experience'
  | 'magic'
  | 'printable'
  | 'planner'
  | 'schedule'
  | 'conflict'
  | 'availability'
  | 'schools';

type ExperienceMode = 'edit' | 'view';

/** Which panel the checkout modal is showing. */
type CheckoutStep = 'auth' | 'purchase';

/** Shared by every Magic bar (add to calendar) event. */
type MagicBarContext = {
  experienceId: string;
  isAuthenticated: boolean;
  // Directory year tag (e.g. `us-schools-2026-2027`); null off directory pages.
  // Required rather than optional so no event can silently omit it.
  directory: string | null;
};

// key = event name, value = event properties
type Events = {
  pageview: {
    page: Page;
    href: string;
  };
  'util:focus': never;
  'dashboard:click cta-magic': never;
  'dashboard:click cta-schedule': never;
  'dashboard:click create-card': {
    product: Product;
    config?: ExperienceConfigName;
  };
  'dashboard:click create-new': never;
  'dashboard:click item-card': never;
  'dashboard:click item-card delete': {
    action: 'delete' | 'leave' | 'unfollow';
  };
  'dashboard:click item-card rename': never;
  'dashboard:click output-card': {
    output: MagicOutput;
  };
  'dashboard:submit magic-prompt': {
    mode: ArtifactType;
    hasFile: boolean;
  };
  'dashboard:click workspace-tab': {
    workspace: 'personal' | 'organization' | 'family';
  };
  'experience:click nav-logo': {
    isAuthenticated: boolean;
    mode: ExperienceMode;
  };
  'experience:filter-update': {
    experienceId: string;
  };
  'experience:filter-clear': {
    experienceId: string;
  };
  'experience:hidden-events-toggle': {
    experienceId: string;
    isShowingHidden: boolean;
  };
  'experience:click nav-library': never;
  'experience:click nav-organization': never;
  'experience:click nav-share-link': never;
  'experience:click card-edit': {
    target: 'icon' | 'card';
  };
  'experience:click card-view': never;
  'experience:click card-delete': never;
  'experience:click card-remove': never;
  'experience:edit card-field': {
    field:
      | 'emoji'
      | 'title'
      | 'datetime'
      | 'location'
      | 'description'
      | 'kind'
      | 'shortTitle'
      | 'category'
      | 'labels'
      | 'hidden';
  };
  'experience:author click edit-control': {
    control: string;
  };
  'experience:author change styles': {
    setting: 'bold' | 'sparkle' | 'color';
  };
  'experience:author change layout': {
    pageLayout: ExperienceLayouts;
    collectionLayout: BlockLayouts;
  };
  'experience:author change toggles': {
    setting:
      | 'page-banner'
      | 'page-hero'
      | 'page-logo'
      | 'page-attribution'
      | 'page-description'
      | 'page-subscribe'
      | 'event-emoji'
      | 'event-timing'
      | 'event-description'
      | 'event-location';
  };
  'experience:author change event-settings': {
    setting: 'timezone' | 'timezone-display' | 'history-toggle';
  };
  'experience:author change interaction-add-event-settings': {
    setting: 'mode';
  };
  'experience:click powered-by': never;
  /** The Magic panel's non-AI escape hatch — opens the create-event modal. */
  'experience:click add-manually': {
    experienceId?: string;
  };
  'experience:click open-collaborator-share': never;
  /**
   * The text-reminder move prompt: a planner homed in a personal workspace by
   * someone who has a family one. `canMove` is false for a collaborator, who
   * sees only the explanation.
   */
  'experience:view move-to-family-prompt': {
    canMove: boolean;
  };
  'experience:click move-to-family': never;
  'experience:click open-calendars': never;
  'experience:click early-hero-confirm': {
    experienceId: string;
  };
  'experience:click early-hero-report': {
    experienceId: string;
  };
  'experience:cta save-experience success': {
    experienceId: string;
  };
  'experience:view': {
    mode: ExperienceMode;
    experienceId: string;
    slug?: string;
  };
  /**
   * `feature` is the gate that opened, and is the only property that identifies
   * it. `launch` is a post-checkout resume key, not an identifier: three gates
   * share `share-modal` and the ones with no resume target report `''`, so it
   * collapses distinct features together. Kept alongside rather than replaced,
   * because existing funnels are built on it.
   */
  'entry:click upgrade': {
    target: 'button';
    feature: PaywallFeature;
  };
  'experience:click upgrade': {
    target: 'button' | 'link';
    feature: PaywallFeature;
    launch?: string;
    experienceType?: ExperienceTypes;
    config?: ExperienceConfigName;
  };
  /** `feature` is the gate that opened the modal, absent for a generic open. */
  'upgrade-modal:click purchase': {
    launch?: string;
    feature?: PaywallFeature;
    experienceType?: ExperienceTypes;
    config?: ExperienceConfigName;
  };
  'launcher:action': {
    action: LaunchOption;
  };
  'experience:print': {
    experienceId: string;
  };
  'interaction:add-event click subscribe-all': {
    isAuthenticated: boolean;
    experienceId: string;
    mode: ExperienceMode;
    channel: 'google-feed' | 'apple' | 'outlook' | 'office' | 'other';
  };
  'interaction:magic add-event click': {
    isAuthenticated: boolean;
    experienceId: string;
    mode: ExperienceMode;
    platform: PlatformTypes;
  };
  'interaction:magic platform-picker select': {
    platform: PlatformTypes;
    isAuthenticated: boolean;
  };
  'interaction:add-event click add': {
    isAuthenticated: boolean;
    experienceId: string;
    mode: ExperienceMode;
    method: 'invite' | 'copy';
    channel: PlatformTypes;
  };
  'interaction:bulk-add-event click add': {
    isAuthenticated: boolean;
    experienceId: string;
    mode: ExperienceMode;
    method: 'invite' | 'copy';
    channel: PlatformTypes;
  };
  'interaction:bulk-add-event click confirm': {
    isAuthenticated: boolean;
    experienceId: string;
    mode: ExperienceMode;
    method: 'invite' | 'copy';
    channel: PlatformTypes;
  };
  'interaction:save-event click save': {
    isAuthenticated: boolean;
    experienceId: string;
    mode: ExperienceMode;
  };
  'interaction:save-event success': {
    experienceId: string;
    fromEventId: string;
    toEventId: string;
  };
  'schedule:create': {
    type: ScheduleType;
    workflow: ScheduleOption;
  };
  'schedule:share': never;
  'schedule:click copy-share-link': never;
  'schedule:signup-cta': never;
  'schedule:click auth-link': {
    scheduleId: string;
    action: 'login' | 'register';
  };
  'schedule:change': never;
  'schedule:focus-update': never;
  'schedule:focus-clear': never;
  'schedule:click follow-schedule': {
    scheduleId: string;
    isAuthenticated: boolean;
  };
  'schedule:cta follow-schedule success': {
    scheduleId: string;
  };
  'schedule:click unfollow-schedule': {
    scheduleId: string;
  };
  'schedule:click copy-schedule': {
    scheduleId: string;
  };
  'schedule:click copy-schedule success': {
    scheduleId: string;
  };
  'schedule:click follow-entry': {
    scheduleId: string;
    isAuthenticated: boolean;
    variant: string;
  };
  'schedule:click follow-entry success': {
    scheduleId: string;
  };
  'schedule:click copy-link': {
    scheduleId: string;
    entryId: string;
  };
  'schedule:click change header': {
    headerId: ScheduleHeaderId;
  };
  'schedule:click add header': never;
  'schedule:click remove header': never;
  'prompt:click try-sample': never;
  'prompt:click back': never;
  'prompt:click create': {
    promptLength: number;
  };
  'prompt:click enter-schedule': never;
  'prompt:receive error': {
    code: string | null;
  };
  'entry:new': {
    composer: DraftComposer;
  };
  'entry:create': {
    composer: DraftComposer;
  };
  'entry:update': never;
  'entry:delete': never;
  'entry:delete-instance': never;
  'entry:hide': never;
  'entry:unhide': never;
  'entry:click': never;
  'ai-add:convert content': {
    conversionType: ConversionType | 'unknown';
  };
  'ai-add:click add to schedule': never;
  'ai-add:click entry edit': never;
  'ai-add:click entry delete': never;
  'ai-add:click entry messages': never;
  'ai-add:click add manually': never;
  'ai-add:inline edit': never;
  'update-banner:view': never;
  'update-banner:reload': never;
  'source:create': {
    type: 'ics' | 'google' | 'agendahero';
  };
  'source:update': never;
  'source:delete': never;
  'feed-export:click': {
    medium: 'apple' | 'google' | 'outlook' | 'office' | 'other';
  };
  'settings:click upgrade': {
    target: 'button' | 'link';
    launch?: string;
  };
  'snippet:create': never;
  'onboarding:next-step': {
    flow: OnboardingFlow | null;
    index: number;
  };
  'onboarding:prev-step': {
    flow: OnboardingFlow | null;
    index: number;
  };
  'onboarding:complete': {
    flow: OnboardingFlow | null;
  };
  'onboarding:start-flow': {
    flow: OnboardingFlow | null;
  };
  'onboarding:close': {
    flow: OnboardingFlow | null;
  };
  'planner-onboarding:start': {
    experienceId: string;
  };
  'planner-onboarding:step': {
    experienceId: string;
    step: PlannerOnboardingStep;
  };
  'planner-onboarding:skip': {
    experienceId: string;
    step: PlannerOnboardingStep | null;
  };
  'planner-onboarding:finish': {
    experienceId: string;
  };
  'planner-onboarding:try-example': {
    experienceId: string;
  };
  'quick-create:click schedules': {
    from: 'home' | 'settings' | 'shared';
  };
  'quick-create:click example': never;
  'quick-create:click generate': {
    // Set by the /magic composers: the output the prompt is set up to make.
    output?: MagicOutput;
  };
  'quick-create:click output-card': {
    output: MagicOutput;
  };
  /** Results with events and no artifact, where Design offers one. */
  'quick-create:view events-result': {
    chatId: string;
  };
  'quick-create:open design-panel': {
    chatId: string;
  };
  'quick-create:click design-card': {
    output: ArtifactOutput;
    chatId: string;
  };
  'quick-create:click customize-artifact': {
    output: ArtifactOutput;
    experienceId: string;
  };
  'quick-create:make edit': {
    field:
      | 'emoji'
      | 'title'
      | 'datetime'
      | 'kind'
      | 'category'
      | 'labels'
      | 'hidden'
      | 'location'
      | 'description';
  };
  'quick-create:click send-chat': never;
  'quick-create:click send': never; // Confirm send email
  'quick-create:click send to agenda hero': never;
  'quick-create:click send to google-integration': {
    // Click the calendar integration option. The event name is kept for
    // continuity of the existing funnel.
    isBatch: boolean;
    platform: PlatformTypes;
  };
  'quick-create:click send to outlook-integration': {
    // Outlook counterpart — previously Outlook submissions polluted the
    // google-integration funnel above.
    isBatch: boolean;
    platform: PlatformTypes;
  };
  'quick-create:click add to calendar intent': {
    // Click magic add to calendar button, but might be lead-gated
    platform: PlatformTypes;
    isGated: boolean;
  };
  'quick-create:click add to calendar': {
    // Clicks add to calendar button (and is not lead-gated)
    platform: PlatformTypes;
    isPreferred: boolean;
    hasPreferred: boolean;
  };
  'quick-create:click add to preferred': {
    platform: PlatformTypes;
  };
  'quick-create:click add-many': {
    platform: PlatformTypes;
    path: string;
  };
  'quick-create:click again': never;
  'quick-create:click share': {
    channel: 'copy' | 'facebook' | 'linkedin' | 'x';
  };
  'quick-create:click copy item link': never;
  'quick-create:convert content': {
    conversionType: ConversionType | 'unknown';
  };
  'quick-create:convert content success': {
    conversionType: ConversionType | 'unknown';
  };
  'quick-create:convert content polling update': {
    conversionType: ConversionType | 'unknown';
  };
  'quick-create:click details cta': {
    jobId: string;
  };
  'quick-create:click confirm delete entry': never;
  'quick-create:click create-experience': {
    isAuthenticated: boolean;
  };
  'quick-create:click create-planner': {
    isAuthenticated: boolean;
  };
  'quick-create:create artifact': {
    output: string;
  };
  'quick-create:click add-to-experience': {
    isNew: boolean;
    isMulti: boolean;
  };
  'magic-share:click pdf': {
    path: string;
  };
  'magic-share:click feed': {
    path: string;
  };
  'magic-share:click share': {
    path: string;
  };
  'magic-export-modal:open': {
    platform: PlatformTypes;
    isAddAll: boolean;
  };
  'magic-export-modal:click add': {
    platform: PlatformTypes;
    isAddAll: boolean;
  };
  'conflict-schedule-success:click share': {
    channel: 'copy' | 'native' | 'facebook' | 'linkedin' | 'twitter';
    product: Product;
  };
  'lead-gate:show': {
    reason: string | null;
  };
  'lead-gate:success': {
    reason: string | null;
  };
  'web-view:show warning': never;
  'web-view:click browser': never;
  'web-view:click dismiss': never;
  'checkout-experience:click get-planner': {
    experienceId: string;
  };
  'checkout-experience:view planner': {
    experienceId: string;
  };
  /**
   * The /page/:slug/planner landing page, which exists only to sell the planner.
   * This view is the denominator for the CTA click-through rate below.
   */
  'planner-landing:view': CheckoutTarget & {
    variant: PlannerLandingVariant;
    isAuthenticated: boolean;
  };
  'planner-landing:click checkout': CheckoutTarget & {
    variant: PlannerLandingVariant;
    placement: PlannerCheckoutPlacement;
    isAuthenticated: boolean;
    /**
     * Where the click goes, so the modal's funnel is measured against the clicks
     * that actually reach it: a signed-in buyer skips the modal for checkout,
     * and an external checkout leaves the app altogether.
     */
    next: 'auth' | 'checkout' | 'external';
  };
  /**
   * The checkout modal's own funnel: view modal, click purchase, start checkout,
   * with close modal as the drop-off. The auto-attached `path` is what separates
   * the two surfaces that mount it, since the landing page opens the modal on
   * the auth step and the experience's Get printable button on the purchase one.
   */
  'checkout-experience:view modal': CheckoutTarget & {
    step: CheckoutStep;
  };
  'checkout-experience:click purchase': CheckoutTarget & {
    isAuthenticated: boolean;
  };
  /**
   * Leaving for /billing/*, the last of a purchase the client can see. `trigger`
   * says which step released it: the purchase button, the auth panel, or the
   * resume after Google auth reloaded the page.
   */
  'checkout-experience:start checkout': CheckoutTarget & {
    trigger: 'purchase' | 'auth' | 'resume';
  };
  'checkout-experience:close modal': CheckoutTarget & {
    step: CheckoutStep;
  };
  'experience-printable:click print': {
    experienceId: string;
    printableId: string;
    layout: ExperiencePrintableLayouts;
    currentPageOnly: boolean;
  };
  'experience-printable:click download': {
    experienceId: string;
    printableId: string;
    layout: ExperiencePrintableLayouts;
  };
  'magic-bar:view': MagicBarContext;
  'magic-bar:click platform': MagicBarContext & {
    platform: PlatformTypes;
  };
  'magic-bar:click feed': MagicBarContext;
  'magic-bar:click add-all': MagicBarContext & {
    platform: PlatformTypes;
    isSelectMode: boolean;
    selectedCount: number;
  };
  'magic-bar:click select-mode': MagicBarContext & {
    isEntering: boolean;
  };
  'magic-bar:add-all success': MagicBarContext & {
    platform: PlatformTypes;
  };
  'magic-upsell-modal:view': {
    experienceId: string;
  };
  'magic-upsell-modal:click cta': {
    experienceId: string;
  };
  'magic-upsell-modal:dismiss': {
    experienceId: string;
    method: 'close' | 'later';
  };
};

type UserProperties = {
  scheduleCount: number;
  aiScheduleCount: number;
};

const DISABLED = window.location.search.includes('analyticsDisabled=1');

// LogRocket is a large SDK that only does fire-and-forget session recording, so
// it doesn't need to be in the render-blocking entry bundle. Load it lazily on
// first use and cache the module promise so repeated calls share one download.
let logRocketPromise: Promise<typeof LogRocketSdk> | null = null;
const loadLogRocket = () => {
  logRocketPromise ??= import('logrocket')
    .then((module) => module.default)
    .catch((error: unknown) => {
      // Don't cache a failed chunk load — let a later call retry the import.
      logRocketPromise = null;
      throw error;
    });
  return logRocketPromise;
};

// Resolves with the initialized SDK, or with null when the session started on a
// page we don't record (see shouldRecordSession). `identify` shares the promise
// rather than loading the SDK itself, so it can't run ahead of the decision and
// identify a session that was never initialized.
let sessionRecording: Promise<typeof LogRocketSdk | null> | null = null;
const startSessionRecording = () => {
  sessionRecording ??= shouldRecordSession()
    .then(async (shouldRecord) => {
      if (!shouldRecord) {
        return null;
      }

      const LogRocket = await loadLogRocket();

      LogRocket.init(config.logrocket.key, {
        release: `${config.version}-${config.release}`,
        shouldParseXHRBlob: true,
        mergeIframes: true,
        // Recordings keep request bodies, and every auth operation (login,
        // password change and reset, email codes) goes through the one GraphQL
        // endpoint with its secret in `variables`.
        network: {
          requestSanitizer: (request) => {
            request.url = redactSensitiveUrls(request.url);
            if (typeof request.body === 'string') {
              request.body = redactGraphQLRequestBody(request.body);
            }
            return request;
          },
        },
        browser: {
          urlSanitizer: redactSensitiveUrls,
        },
      });
      LogRocket.getSessionURL((sessionURL) => {
        Sentry.getCurrentScope().setExtra('sessionURL', sessionURL);
      });

      // Releases the srcDoc iframes to boot their own instance, now that there
      // is a session for `mergeIframes` to fold them into.
      notifySessionRecordingStarted();

      return LogRocket;
    })
    // Session recording is best-effort; ignore a failed SDK load, and drop the
    // cached promise so a later call can retry the import.
    .catch(() => {
      sessionRecording = null;
      return null;
    });

  return sessionRecording;
};

if (config.amplitude.enabled && !DISABLED) {
  window.addEventListener('pagehide', () => {
    amplitude.setTransport('beacon');
    // Sets https transport to use `sendBeacon` API
    amplitude.flush();
  });
}

export const useAnalytics = () => {
  const { pathname, search } = window.location;

  const init = useCallback(() => {
    if (DISABLED) {
      return;
    }

    if (config.amplitude.enabled) {
      // Default tracking records the page URL, path and referrer on its own,
      // and our events add `path` and `href`; one plugin covers all of them.
      // Enrichment plugins run in the order they were added, and anything added
      // before init runs ahead of the SDK's own. The SDK's page-URL enrichment
      // stamps the current and previous URL onto every event, so it is turned
      // off in init and registered here instead, ahead of the redaction.
      amplitude.add(pageUrlEnrichmentPlugin());
      amplitude.add({
        name: 'redact-sensitive-urls',
        type: 'enrichment',
        execute: async (event) => redactSensitiveUrlsDeep(event),
      });
      // Equivalent to `defaultTracking: true` apart from the one plugin above.
      amplitude.init(config.amplitude.key, {
        autocapture: { pageUrlEnrichment: false },
      });
    }

    if (config.logrocket.enabled) {
      void startSessionRecording();
    }
  }, []);

  const identify = useCallback((user: NonNullable<DecoratedProfile>) => {
    if (DISABLED) {
      return;
    }

    const { id, email, name, status, tier, organizations } = user;

    // `status` is the account state (active/anonymous/invited), which says
    // nothing about what the user pays for. These two are what let a funnel be
    // split by plan, and what makes a free-to-paid flip visible: the provider
    // re-identifies whenever the profile changes, so a purchase updates them
    // as soon as the refetched profile lands.
    //
    // `tier` is the entitlement the app itself gates on, which the server
    // resolves as max(seatTier, inheritedSeatTier) — so Plus inherited from a
    // workspace counts. `plan` is narrower on purpose: the subscription the
    // user pays for themselves, which is the one funding their personal org
    // (the rule useSubscription owns). Someone on inherited Plus therefore has
    // a tier but no plan, which is the honest reading of both.
    const plan = organizations.find(
      (org) => org.type === OrganizationType.Personal
    )?.subscriptionPlan;

    if (config.amplitude.enabled) {
      amplitude.setUserId(id);

      const event = new amplitude.Identify();
      event.set('id', id);
      if (email) {
        event.set('email', email);
      }
      event.set('status', status);
      event.set('tier', tier);
      if (plan) {
        event.set('plan', plan);
      }
      amplitude.identify(event);
    }

    if (config.logrocket.enabled) {
      const traits: Record<string, string | number | boolean> = {
        name: name ?? '',
        status,
        tier,
      };
      if (email) {
        traits.email = email;
      }
      if (plan) {
        traits.plan = plan;
      }
      startSessionRecording()
        .then((LogRocket) => LogRocket?.identify(id, traits))
        // Session recording is best-effort; ignore a failed SDK load.
        .catch(() => undefined);
    }
  }, []);

  const trackEvent = useCallback(
    <E extends keyof Events>(
      eventName: E,
      ...args: Events[E] extends never
        ? []
        : Partial<Events[E]> extends Events[E]
          ? [properties?: Events[E]]
          : [properties: Events[E]]
    ) => {
      if (DISABLED) {
        return;
      }

      const [properties] = args;
      const client = getClientName();

      amplitude.track(eventName, {
        ...properties,
        env: config.env,
        client: client,
        path: location.pathname,
      });
    },
    []
  );

  const trackPageView = useCallback(
    (page: Page, properties?: Record<string, unknown>) =>
      trackEvent('pageview', {
        ...properties,
        page,
        href: `${pathname}${search}`,
      }),
    [trackEvent, pathname, search]
  );

  const incrementUserProperty = useCallback(
    <P extends keyof UserProperties>(propertyName: P, amount = 1) => {
      if (!DISABLED) {
        const event = new amplitude.Identify();
        event.add(propertyName, amount);
        amplitude.identify(event);
      }
    },
    []
  );

  return {
    init,
    identify,
    trackPageView,
    trackEvent,
    incrementUserProperty,
  };
};
